import { z } from 'zod';
import { CLIENT_AD_EVENT_DESTINATIONS, SERVER_AD_EVENT_DESTINATIONS } from './destinations.js';
import { DELIVERY_OPTION, CHECKOUT_PAYMENT_OPTION, PREORDER_PAYMENT_OPTION, TAR_OPTION, ECOMMERCE_PAYLOAD } from './shared-fields.js';

/**
 * Funnel events—ecommerce funnel stages, conversion outcomes, and their
 * internal-only companions. Events with `CLIENT_AD_FANOUT` in `destinations`
 * carry per-platform name translations under `adEventNames` (`meta`, `tiktok`,
 * `tracklution`, `gtm`) and fan out to ad-platform pixels via
 * ConversionTracking. Conversion outcomes carry `SERVER_AD_FANOUT` and fan out
 * to ad platforms server-side via the `conversion.event` Pub/Sub pipeline.
 * Events without `destinations` are CDP-only (the universal Segment → BigQuery
 * + PostHog baseline) and do not trigger any pixel fire.
 *
 * Checkout- and Carthage-surface entries carry the clean `co_*` / `carth_*`
 * canonical with the legacy emitted string held as `wireName`—the wire emits
 * `wireName ?? canonical` (see `getEventName`), so an event can adopt its
 * canonical with zero data discontinuity.
 */
/**
 * The state checkout resolved to before the buyer touched anything, carried on the
 * checkout-start events.
 *
 * These are properties rather than their own `_option_selected` events: a default
 * resolving is not something the buyer did, and firing an event for it made the
 * real selection events fire on every page load. See the "an event is a happening,
 * not a state read" convention.
 *
 * The enums come from `shared-fields.ts`, so these and the buyer-selection events
 * that report a change to them can never describe different value sets. Checkout
 * and preorder carry different payment enums because the two option sets are
 * disjoint, so one wide enum would describe values the surface can never hold.
 *
 * `defaultDeliveryOption` says what checkout resolved to, which is not the same
 * as what the buyer was offered: preorder never renders the delivery section, and
 * neither does regular checkout once the payment option is `IN_PERSON_PAYMENT`,
 * which forces pickup. The value is still the fulfilment the order will use, so
 * it is worth carrying — the wording just must not imply a choice was presented.
 */
const RESOLVED_DELIVERY_STATE = {
    defaultDeliveryOption: DELIVERY_OPTION.describe('The fulfilment option checkout resolved to when it became usable, before any choice of the buyer’s. Present whether or not a delivery choice was offered.'),
};
const RESOLVED_CHECKOUT_STATE = {
    defaultPaymentOption: CHECKOUT_PAYMENT_OPTION.describe('The payment option preselected for the buyer when checkout became usable, before any choice of theirs.'),
    ...RESOLVED_DELIVERY_STATE,
};
const RESOLVED_PREORDER_STATE = {
    defaultPaymentOption: PREORDER_PAYMENT_OPTION.describe('The reservation option preselected for the buyer when preorder checkout became usable, before any choice of theirs.'),
    ...RESOLVED_DELIVERY_STATE,
};
/**
 * How the Express checkout gate routed a buyer who ended up in Legacy checkout.
 * It splits the Legacy population so an Express vs Legacy comparison can set
 * the buyers who were offered Express apart from those who never could be.
 */
const EXPRESS_GATE_OUTCOME = {
    /**
     * @deprecated Unreachable: there is no Express-to-Legacy return path today.
     * Kept in the schema for backward compatibility; the gate no longer emits it.
     */
    FROM_EXPRESS: 'from_express',
    FLAG_OFF: 'flag_off',
    FLAG_TIMEOUT: 'flag_timeout',
    INELIGIBLE: 'ineligible',
    ERROR: 'error',
};
const EXPRESS_GATE_OUTCOME_FIELD = z
    .enum([
    EXPRESS_GATE_OUTCOME.FROM_EXPRESS,
    EXPRESS_GATE_OUTCOME.FLAG_OFF,
    EXPRESS_GATE_OUTCOME.FLAG_TIMEOUT,
    EXPRESS_GATE_OUTCOME.INELIGIBLE,
    EXPRESS_GATE_OUTCOME.ERROR,
])
    .describe('How the Express gate sent this buyer to Legacy checkout. `from_express`: (deprecated, unreachable) Express handed the buyer over to place the order. `flag_off`: the cart could have gone to Express, but the express-checkout-experiment flag resolved false. `flag_timeout`: the cart could have gone to Express, but the flag did not resolve within the gate timeout. `ineligible`: the cart could not go to Express. `error`: the gate failed (cart fetch error, render error).');
/**
 * The full set of Express gate outcomes, including the redirect-to-Express case
 * that EXPRESS_GATE_OUTCOME does not include (since that enum only describes
 * why a buyer ended up in Legacy).
 */
const EXPRESS_GATE_DECIDED_OUTCOME = {
    EXPRESS: 'express',
    FLAG_OFF: 'flag_off',
    FLAG_TIMEOUT: 'flag_timeout',
    INELIGIBLE: 'ineligible',
    ERROR: 'error',
};
const EXPRESS_GATE_DECIDED_OUTCOME_FIELD = z.enum([
    EXPRESS_GATE_DECIDED_OUTCOME.EXPRESS,
    EXPRESS_GATE_DECIDED_OUTCOME.FLAG_OFF,
    EXPRESS_GATE_DECIDED_OUTCOME.FLAG_TIMEOUT,
    EXPRESS_GATE_DECIDED_OUTCOME.INELIGIBLE,
    EXPRESS_GATE_DECIDED_OUTCOME.ERROR,
]);
/**
 * Which step timed out or failed when the outcome is error.
 */
const EXPRESS_GATE_FAILURE_STEP_FIELD = z
    .enum(['app_check', 'cart_fetch', 'services_provider', 'flag_resolution'])
    .describe('Which step timed out or failed. `app_check`: the Firebase App Check token fetch failed. `cart_fetch`: the cart request failed or timed out. `services_provider`: the services provider request failed. `flag_resolution`: the PostHog flag did not resolve in time.');
/**
 * Why a cart was ineligible for Express.
 */
const EXPRESS_GATE_INELIGIBILITY_REASON = z.enum([
    'no_cart_id',
    'multiple_vehicles',
    'has_loose_accessories',
    'has_discounts',
    'has_promotions',
    'missing_inventory_item_id',
    'missing_variant_config_id',
    'cart_disabled',
    'unsupported_order_type',
    'reservation_not_armed',
    'missing_reservation_fee',
]);
/**
 * What the buyer had chosen when they pressed the order button.
 *
 * These are properties on the submit event rather than a replay of the selection
 * events, because those are capped: past the cap the last `_selected` no longer
 * reflects the final choice. This is the authoritative snapshot, and it is also
 * where TAR's resolved default lives — unlike payment and delivery it cannot ride
 * the start event, since its supported set depends on the registration address.
 */
const SUBMITTED_SELECTIONS = {
    paymentOption: CHECKOUT_PAYMENT_OPTION.describe('The payment option they submitted with, whether they chose it or it was preselected.'),
    disclosuresAgreed: z
        .boolean()
        .describe('Whether the disclosures box was ticked at submission. The authoritative consent state — the toggle event is capped and cannot answer this.'),
    appliedPromotionIds: z
        .array(z.string())
        .describe('Promotions the order carried, so revenue effects attribute to them.'),
};
/**
 * What was bought, on the events that report a placement.
 *
 * Mirrors the same fields on the checkout-start events so the top and bottom of
 * the funnel are directly comparable — "what was in the cart when they arrived"
 * and "what was in it when they ordered" answered in the same shape.
 *
 * The GA4 `opts.ecommerce` leg already carries item detail, but that goes to
 * the ad platforms and never to the CDP, so without these the warehouse cannot
 * see what was actually sold. Product facts only: these entries fan out to ad
 * platforms, where [convention 9](../../../../knowledge/shared/engineering/analytics-event-conventions.md#9-pii-tiering)
 * permits nothing about the buyer.
 */
const PLACED_ORDER_CONTENTS = {
    numVehicles: z.number().int().describe('How many vehicles the order covers.'),
    numAccessories: z
        .number()
        .int()
        .describe('Total accessory count across the order, summing per-accessory quantities.'),
    vehicleProductIds: z
        .array(z.string())
        .describe('Product id of each vehicle ordered, for joining to inventory.'),
    paymentOption: z
        .string()
        .describe('The payment method the order was placed with. A property rather than a separate event: cash and financed orders are one funnel.'),
};
const FUNNEL_EVENTS = {
    // -------------------------------------------------------------------------
    // Conversion outcomes—high-value business milestones forwarded to Google
    // Ads as offline conversions via the Enhanced Conversions for Leads flow
    // (`uploadClickConversions`). See `CONVERSION_EVENT_TO_ACTION_KEY` and
    // `GoogleAdsAdapter`. The conversion payload (order id/value, hashed-matchable
    // buyer contact, attribution) rides the Pub/Sub leg, not the event props.
    // -------------------------------------------------------------------------
    CREDIT_APP_SUBMITTED: {
        canonical: 'financing_credit_app_submitted',
        wireName: 'credit_app_submitted',
        surface: 'backend',
        destinations: SERVER_AD_EVENT_DESTINATIONS,
        description: 'Buyer submitted their credit application to Ekho.',
    },
    DOWN_PAYMENT_RECEIVED: {
        canonical: 'payment_down_payment_received',
        wireName: 'down_payment_received',
        surface: 'backend',
        destinations: SERVER_AD_EVENT_DESTINATIONS,
        description: "Buyer's down payment was received.",
    },
    FINANCING_DOCS_SIGNED: {
        canonical: 'financing_docs_signed',
        surface: 'backend',
        destinations: SERVER_AD_EVENT_DESTINATIONS,
        description: 'Buyer signed the financing documents.',
    },
    /** Grouped under cash-order conversion actions in the Google Ads configuration. */
    BUYER_SIGNED_PURCHASE_AGREEMENT: {
        canonical: 'vehicle_purchase_agreement_signed',
        wireName: 'buyer_signed_purchase_agreement',
        surface: 'backend',
        destinations: SERVER_AD_EVENT_DESTINATIONS,
        description: 'Buyer signed the purchase agreement.',
    },
    /**
     * The server-side lead conversion. Browser `Lead` pixel events fire from
     * several client triggers. `LeadService.createLead` publishes this when the
     * lead is still `PENDING`, then marks it `SENT` (see `lead-conversion.ts`
     * in backend-common). Two creates that both observe `PENDING` can both
     * publish; Google Ads dedupes on the lead id. The publish goes through
     * `publishConversionEvent` directly, because `serverAnalytics.track`
     * swallows a failure the retry marker depends on. Both CDP sinks are off.
     * The CDP fact for the same lead is `lead_created`.
     */
    LEAD_CONVERSION_CREATED: {
        canonical: 'lead_conversion_created',
        surface: 'backend',
        destinations: SERVER_AD_EVENT_DESTINATIONS,
        cdp: { posthog: false, segment: false },
        description: 'An Ekho-originated lead was created and sent to the ad conversion pipeline.',
    },
    // -------------------------------------------------------------------------
    // GA4 ecommerce funnel steps—ad-platform pixel fan-out
    // -------------------------------------------------------------------------
    /** Fired by Carthage (via ConversionTracking) when a buyer views a vehicle detail page. */
    VIEW_ITEM: {
        canonical: 'view_item',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'ViewContent',
            tiktok: 'ViewContent',
            tracklution: null,
            gtm: 'view_item',
        },
    },
    /** Not yet instrumented. Reserved for future use. */
    SELECT_ITEM: {
        canonical: 'select_item',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: null,
            tiktok: null,
            tracklution: null,
            gtm: 'select_item',
        },
    },
    /** Not yet instrumented. Reserved for future use. */
    VIEW_ITEM_LIST: {
        canonical: 'view_item_list',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: null,
            tiktok: null,
            tracklution: null,
            gtm: 'view_item_list',
        },
    },
    /** Fired by Carthage (via ConversionTracking) when a buyer clicks the checkout CTA on a listing. */
    ADD_TO_CART: {
        canonical: 'add_to_cart',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'AddToCart',
            tiktok: 'AddToCart',
            tracklution: null,
            gtm: 'add_to_cart',
        },
    },
    /**
     * Fired the moment the Express gate makes its routing decision, on every path
     * (express, flag_off, ineligible, error). This is the dedicated gate outcome
     * event that fires immediately when the decision is made, independent of
     * whether checkout later fires `co_checkout_started`.
     *
     * The event carries full debugging context: identity, eligibility details,
     * flag state, failure details with per-step timing, and the final outcome.
     * Enough to debug any single cart without additional log correlation.
     *
     * Fires once per visit, held on the tracker instance.
     */
    EXPRESS_GATE_DECIDED: {
        canonical: 'co_express_gate_decided',
        surface: 'checkout',
        description: 'The Express gate made its routing decision. Fires once per visit, the moment the decision is locked. Carries full debugging context.',
        schema: z.object({
            // --- Identity ---
            cartId: z
                .string()
                .optional()
                .describe('The CheckoutCart being routed. Absent when no cartId was in the URL.'),
            programOwnerId: z
                .string()
                .nullable()
                .describe('The program owner id from __OEM_CONFIG__. Null when not configured.'),
            domainOwnerSvpId: z
                .string()
                .nullable()
                .describe('The domain-owner services provider id for this hostname. Null when not a domain-scoped site or services provider fetch failed.'),
            distinctId: z
                .string()
                .optional()
                .describe('The PostHog distinct id for this visitor. Absent when PostHog is not initialized.'),
            entrySource: z
                .enum(['legacy', 'express'])
                .describe('Whether this event fired from the legacy checkout gate or the express entry.'),
            // --- Eligibility ---
            isEligible: z.boolean().describe('Whether the cart was eligible for Express checkout.'),
            ineligibilityReasons: z
                .array(EXPRESS_GATE_INELIGIBILITY_REASON)
                .optional()
                .describe('Why the cart was ineligible. Absent when eligible. Values: no_cart_id, multiple_vehicles, has_loose_accessories, has_discounts, has_promotions, missing_inventory_item_id, missing_variant_config_id, cart_disabled (the cart is disabled), unsupported_order_type (an order type Express does not sell, or a PREORDER cart with no preorder), reservation_not_armed (a deposit cart whose link lacks preorder=1), missing_reservation_fee (a deposit cart with no positive deposit).'),
            // --- Flag state ---
            flagRead: z
                .boolean()
                .describe('Whether the PostHog flag was read. False when cart ineligible, preview mode, or error before flag check.'),
            flagKey: z
                .string()
                .optional()
                .describe('The PostHog flag key. Absent when flag was not read.'),
            flagValue: z
                .union([z.boolean(), z.string()])
                .optional()
                .describe('The flag value or variant. Absent when flag was not read or did not resolve.'),
            flagSkipReason: z
                .enum(['preview_mode', 'cart_ineligible', 'cart_error', 'local_dev_bypass'])
                .optional()
                .describe('Why the flag was not read. Absent when flag was read.'),
            posthogInitialized: z
                .boolean()
                .describe('Whether PostHog was initialized. False in preview mode.'),
            // --- Failure detail ---
            failureStep: EXPRESS_GATE_FAILURE_STEP_FIELD.optional().describe('Which step failed or timed out. Absent when no failure.'),
            failureReason: z
                .enum(['timeout', 'error'])
                .optional()
                .describe('Whether the failure was a timeout or an error. Absent when no failure.'),
            errorMessage: z
                .string()
                .optional()
                .describe('The error message when failureReason is error. Absent otherwise.'),
            httpStatus: z
                .number()
                .int()
                .optional()
                .describe('The HTTP status code when failureStep is cart_fetch or services_provider.'),
            appCheckMs: z
                .number()
                .int()
                .optional()
                .describe('Milliseconds spent warming the App Check token. Absent if not measured.'),
            cartFetchMs: z
                .number()
                .int()
                .optional()
                .describe('Milliseconds spent fetching the cart. Absent if cart fetch did not start.'),
            servicesProviderMs: z
                .number()
                .int()
                .optional()
                .describe('Milliseconds spent fetching the services provider. Absent if the fetch did not start.'),
            totalElapsedMs: z.number().int().describe('Total milliseconds from gate start to decision.'),
            budgetMs: z.number().int().describe('The timeout budget in milliseconds (1500).'),
            // --- Outcome ---
            outcome: EXPRESS_GATE_DECIDED_OUTCOME_FIELD.describe('Where the gate sent the buyer. `express`: redirected to Express checkout. `flag_off`: eligible but the experiment flag was off. `flag_timeout`: eligible but the flag did not resolve in time. `ineligible`: the cart could not use Express. `error`: the gate failed.'),
            route: z
                .enum(['express', 'legacy'])
                .describe('The route taken. `express` if redirecting, `legacy` otherwise.'),
        }),
    },
    /**
     * Regular checkout only. Preorder has its own start event
     * (`co_preorder_checkout_started`) rather than a `sessionType` discriminator on
     * this one: the two are analytically distinct funnels, and collapsing them into
     * one event blurs a boundary every downstream report wants to keep.
     *
     * Anchored to the `checkout.fe.skeleton.dismissed` performance mark, which fires
     * once every skeleton region the route registered has cleared. That is the one
     * signal that actually means "the buyer can use this page", so the event is true
     * by construction rather than by a proxy that happens to land nearby.
     *
     * A bounded fallback fires it anyway if dismissal never happens (no active
     * performance transaction, or no skeleton region registered). Without one, an
     * instrumentation gap would silently drop the top-of-funnel ad conversion.
     * `trigger` records which path fired, so the fallback's share is measurable.
     *
     * Dedupe: once per visit, held on the tracker instance rather than component
     * state — the checkout tree can mount more than once during a load, and a
     * component-scoped guard resets with it.
     */
    BEGIN_CHECKOUT: {
        canonical: 'co_checkout_started',
        wireName: 'begin_checkout',
        surface: 'checkout',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'InitiateCheckout',
            tiktok: 'InitiateCheckout',
            tracklution: 'checkout_started',
            gtm: 'begin_checkout',
        },
        description: 'Checkout finished loading and is ready to use: every loading placeholder has been replaced with real content. Regular (non-preorder) checkout, legacy or Express, once per visit.',
        schema: z.object({
            cartId: z.string().describe('The CheckoutCart being purchased.'),
            numVehicles: z.number().describe('How many vehicles are in the cart.'),
            numAccessories: z
                .number()
                .describe('Total accessory count across the cart, summing per-accessory quantities.'),
            totalCartCost: z.number().describe('Cart total in USD at the moment checkout became usable.'),
            vehicleProductIds: z
                .array(z.string())
                .describe('Product id of each vehicle in the cart, for joining to inventory.'),
            ...RESOLVED_CHECKOUT_STATE,
            trigger: z
                .enum(['skeleton_dismissed', 'fallback_totals_derived', 'first_block_painted'])
                .describe('Which path fired the event. `fallback_totals_derived` means the skeleton-dismissed mark never arrived. `first_block_painted` is Express, which fires when its first step paints.'),
            expressGateOutcome: EXPRESS_GATE_OUTCOME_FIELD,
        }),
    },
    /**
     * The configurator's counterpart to `co_checkout_started`: the moment the build
     * surface is usable, once per visit. It is the denominator the configurator
     * funnel has never had — without it, `co_configurator_continue_clicked` has no
     * population to divide by, so "what share of buyers who opened a build finished
     * it" is unanswerable.
     *
     * Anchored to both selectors resolving, which is the configurator's equivalent
     * of skeleton dismissal: until the vehicle and model-year selectors know whether
     * they have anything to offer, the page is still deciding what to render. There
     * is no fallback path and so no `trigger` property — the configurator registers
     * no skeleton regions, so there is only ever one way this fires.
     *
     * CDP-only. `co_checkout_started` carries the top-of-funnel ad conversion, and
     * fanning a second, earlier event out to the same platforms would report two
     * conversions for one buyer's journey through a flow they mostly complete.
     *
     * Carries what the buyer was handed before touching anything, on the same
     * reasoning as `RESOLVED_CHECKOUT_STATE`: a default resolving is not something
     * the buyer did, so it rides this event rather than firing a selection event.
     * @dedupe Once per visit.
     */
    CONFIGURATOR_STARTED: {
        canonical: 'co_configurator_started',
        surface: 'checkout',
        description: 'The configurator finished loading and is ready to build on: both selectors have resolved and the starting configuration is on screen. Once per visit.',
        schema: z.object({
            productId: z.string().describe('The vehicle product the buyer landed on.'),
            vehicleName: z.string().describe('Catalog model name of that vehicle.'),
            basePrice: z.number().describe('Vehicle base price in USD, before any accessories.'),
            startingPrice: z
                .number()
                .describe('Build price in USD as first presented, base plus whatever the configurator preselected.'),
            numCategories: z.number().int().describe('How many option categories the build offers.'),
            numPreselectedAccessories: z
                .number()
                .int()
                .describe('How many accessories were already on the build before the buyer touched anything, typically the required categories’ defaults.'),
            canSelectVehicle: z
                .boolean()
                .describe('Whether the buyer was offered a choice of vehicle, or landed locked to one.'),
            canSelectModelYear: z
                .boolean()
                .describe('Whether the buyer was offered a choice of model year. False when only one year is in stock, which is why a missing model-year selection is not always a buyer declining to make one.'),
        }),
    },
    /**
     * Reported on the blur that completes the contact block: email, phone, and legal
     * name all present and all passing the form's own validator. Blur rather than
     * change, because on change the event fires on the first character typed into the
     * last empty field. Validity is decided by calling the same validation function
     * that drives the inline field errors, so the event cannot disagree with what the
     * buyer is looking at.
     *
     * Dedupe: once per visit, held on the tracker instance rather than component
     * state, so the guard survives a remount of the contact section.
     *
     * No TikTok mapping. TikTok has no Contact equivalent, and the nearest candidate,
     * `AddToCart`, is already the mapping for `add_to_cart`—so using it here made
     * TikTok's AddToCart count both a cart add and a contact-info completion, leaving
     * that step unable to measure either. An unmapped step is better than a conflated
     * one; convention 3 permits many-to-one only for one external step reached by
     * genuinely different workflows, which these are not.
     */
    CONTACT_INFO_COMPLETED: {
        canonical: 'co_contact_info_completed',
        wireName: 'contact_info_submitted',
        surface: 'checkout',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'Contact',
            tiktok: null,
            tracklution: 'checkout_contact_info_submitted',
            gtm: 'contact_info_submitted',
        },
        description: 'Buyer finished entering their contact details: email, phone number, and legal name are all filled in and valid. Regular checkout only, once per visit.',
        schema: z.object({
            cartId: z.string().describe('The CheckoutCart being purchased.'),
        }),
    },
    /**
     * The `po_` counterpart to `co_contact_info_completed`, for the preorder funnel.
     *
     * Same three fields, same validator, same blur trigger: the contact block does
     * not differ between the flows today, so this pair is separated for reporting
     * rather than because the collected data diverges. If preorder later collects a
     * different set, this entry is where that shows up.
     *
     * `surface` stays `checkout` — preorder runs inside the checkout app. Carries no
     * `wireName`, being a new event, so its canonical is the emitted name.
     *
     * The external names deliberately match its `co_` twin. Meta receives `Contact`
     * from both, which convention 3 permits: one external funnel step reached by two
     * genuinely different workflows. The Tracklution and gtm names are shared because
     * changing them needs configuration on the far side — a new Tracklution event has
     * to exist there, and a new dataLayer name would silently stop firing dealer GTM
     * triggers built on the current one. So the split separates the CDP funnels; the
     * ad platforms still see a single contact step until someone configures otherwise.
     */
    PREORDER_CONTACT_INFO_COMPLETED: {
        canonical: 'po_contact_info_completed',
        surface: 'checkout',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'Contact',
            tiktok: null,
            tracklution: 'checkout_contact_info_submitted',
            gtm: 'contact_info_submitted',
        },
        description: 'Buyer finished entering their contact details on a preorder: email, phone number, and legal name are all filled in and valid. Fires once per visit.',
        schema: z.object({
            cartId: z.string().describe('The CheckoutCart being preordered.'),
        }),
    },
    /**
     * The registration and shipping address milestones are separate events rather
     * than one event carrying the address surface, because their ad-name mappings
     * differ in every slot: `add_shipping_info` is a standard GA4 ecommerce step
     * distinct from a generic address capture, and Tracklution names them
     * separately too. A registry entry holds one `adEventNames` map, so the two
     * cannot share it.
     *
     * Both fire once per distinct valid address, not once per visit: a buyer who
     * changes their address should re-report it. Repeats are safe here because
     * neither fans out to a bid optimizer (`meta` and `tiktok` are null), and GA4
     * treats ecommerce events as repeatable, converting its funnel reports on
     * sessions and users rather than raw event counts.
     */
    REGISTRATION_ADDRESS_COMPLETED: {
        canonical: 'co_registration_address_completed',
        surface: 'checkout',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: null,
            tiktok: null,
            tracklution: 'checkout_address_info_submitted',
            gtm: 'address_info_submitted',
        },
        description: 'Buyer finished entering their registration address and it is valid. Fires once per distinct valid address, so a genuine change reports again.',
        schema: z.object({ cartId: z.string().describe('The cart being registered.') }),
        ecommerceSchema: ECOMMERCE_PAYLOAD,
    },
    SHIPPING_ADDRESS_COMPLETED: {
        canonical: 'co_shipping_address_completed',
        surface: 'checkout',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: null,
            tiktok: null,
            tracklution: 'checkout_shipping_info_submitted',
            gtm: 'add_shipping_info',
        },
        description: 'Buyer finished entering their shipping address and it is valid, or reused their home address. Fires once per distinct valid address, so a genuine change reports again.',
        schema: z.object({
            cartId: z.string().describe('The cart being shipped.'),
            fulfilmentOption: DELIVERY_OPTION.describe('The fulfilment the address is for. Also sent to the ad platforms as `shipping_tier`, which is the GA4 name for it.'),
        }),
        ecommerceSchema: ECOMMERCE_PAYLOAD,
    },
    /**
     * Emitted only on payment paths that render a payment form: the Stripe
     * PaymentElement, or the Clerq widget for ACH. Klarna, crypto, in-person, and
     * zero-due-today carts collect no payment details in checkout and so have no
     * form-completion moment to report.
     */
    PAYMENT_INFO_PROVIDED: {
        canonical: 'co_payment_info_completed',
        wireName: 'add_payment_info',
        surface: 'checkout',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'AddPaymentInfo',
            tiktok: 'AddPaymentInfo',
            tracklution: 'payment_info_submitted',
            gtm: 'add_payment_info',
        },
        description: 'Buyer finished entering their payment details. Fires once per visit, when the payment form reports complete.',
        schema: z.object({
            cartId: z.string().describe('The cart being paid for.'),
            paymentType: CHECKOUT_PAYMENT_OPTION.describe('The payment method whose form they completed. Also sent to the ad platforms as `payment_type`, and kept here so the warehouse can query it.'),
        }),
        ecommerceSchema: ECOMMERCE_PAYLOAD,
    },
    /**
     * The true purchase conversion. Fires from the buyer portal at the buyer's
     * terminal high-intent step: clicking through the native financing signatures
     * page after signing (financed orders), or completing identity verification
     * (cash orders). It is not fired at checkout order placement (which fires
     * `co_order_placed`). Keyed per purchased vehicle, each valued at that
     * vehicle's total. `wireName` stays `purchase`: the emitted CDP name and the
     * dedup event_id (`purchase_{vehicleId}`) both derive from it, so the canonical
     * can carry the buyer-portal surface prefix with no data or dedup discontinuity.
     */
    PURCHASE: {
        canonical: 'bp_purchase_complete',
        wireName: 'purchase',
        surface: 'buyer-portal',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'Purchase',
            tiktok: 'Purchase',
            tracklution: 'checkout_completed',
            gtm: 'purchase',
        },
        description: 'Buyer reached their terminal high-intent step: signed the native financing documents (financed) or verified identity (cash).',
    },
    /**
     * Order placed in checkout—a lead-class signal, not the purchase conversion:
     * placement is a commitment, not a completed sale. Fans out as a `Lead` (valued
     * at the amount due today) so full-value Purchase optimization stays anchored to
     * the downstream buyer-portal purchase-complete milestone. TikTok uses `Lead`,
     * not the soft-deprecated sales event `PlaceAnOrder`.
     */
    ORDER_PLACED: {
        canonical: 'co_order_placed',
        surface: 'checkout',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'Lead',
            tiktok: 'Lead',
            tracklution: 'lead',
            gtm: 'order_placed',
        },
        description: 'Buyer placed an order in checkout (pre-fulfillment lead signal). One event for both cash and financed orders — the payment method is a property, not a separate funnel.',
        schema: z.object({
            cartId: z.string().describe('The cart that became an order.'),
            placedOrderId: z
                .string()
                .describe('The `PlacedOrders` record the cart became. The join key between this browsing session and the order: every other event on this device carries the anonymous and session ids, and this is the one that also names the order, so a funnel can be read end to end without a lookup.'),
            buyerId: z
                .string()
                .describe('The `Buyers` record the order was placed under. Present here as well as on the `identify` call, because a property is queryable on the event itself while an identity merge only resolves in destinations that honour aliasing.'),
            purchasedVehicleId: z
                .string()
                .describe('The `PurchasedVehicles` record this order created, and what downstream fulfilment, titling and buyer-portal events key on. Singular because an order carries exactly one vehicle today; the checkout blocks a second one. Distinct from `vehicleProductIds`, which names the catalogue product rather than the purchased unit.'),
            orderValue: z.number().describe('Order value in USD, matching the ad-leg `value`.'),
            ...PLACED_ORDER_CONTENTS,
            deliveryOption: DELIVERY_OPTION.describe('The fulfilment the order was placed with.'),
            tarOption: TAR_OPTION.optional().describe('The paperwork handling the order was placed with. Absent when the supported set never resolved.'),
        }),
        ecommerceSchema: ECOMMERCE_PAYLOAD,
    },
    /**
     * Preorder placed in checkout—a refundable deposit, not a purchase. Fans out as
     * a `Lead` valued at the deposit (never the vehicle price) so a reservation
     * can't inflate value-based bidding as a completed sale.
     */
    PREORDER_PLACED: {
        canonical: 'po_order_placed',
        wireName: 'co_preorder_placed',
        surface: 'checkout',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'Lead',
            tiktok: 'Lead',
            tracklution: 'lead',
            gtm: 'preorder_placed',
        },
        description: 'Buyer placed a preorder (refundable-deposit lead signal).',
        schema: z.object({
            cartId: z.string().describe('The cart that became a preorder.'),
            preorderId: z
                .string()
                .describe('The `Preorders` record the cart became, giving the preorder funnel the same session-to-record join the order funnel has. Singular for the same reason as `purchasedVehicleId` on `co_order_placed`.'),
            buyerId: z.string().describe('The `Buyers` record the preorder was placed under.'),
            depositValue: z
                .number()
                .describe('Deposit taken in USD, never the vehicle price — the ad leg values this as a Lead at the deposit so a reservation cannot inflate value-based bidding.'),
            ...PLACED_ORDER_CONTENTS,
        }),
        ecommerceSchema: ECOMMERCE_PAYLOAD,
    },
    /** Fired by Carthage (via ConversionTracking) when a buyer submits an inquiry or the prequal form. */
    GENERATE_LEAD: {
        canonical: 'generate_lead',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'Lead',
            tiktok: null,
            tracklution: 'lead',
            gtm: 'generate_lead',
        },
    },
    /** Not yet instrumented. Reserved for future use. */
    VIEW_PROMOTION: {
        canonical: 'view_promotion',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: null,
            tiktok: null,
            tracklution: null,
            gtm: 'view_promotion',
        },
    },
    /** Not yet instrumented. Reserved for future use. */
    SELECT_PROMOTION: {
        canonical: 'select_promotion',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: null,
            tiktok: null,
            tracklution: null,
            gtm: 'select_promotion',
        },
    },
    // -------------------------------------------------------------------------
    // Internal-only funnel companions—these fire alongside a GA4 ad event but
    // do not independently fan out to pixels. The GA4 companion handles the
    // pixel fire via ConversionTracking.
    // -------------------------------------------------------------------------
    /**
     * The preorder funnel's entry event, and the `po_` counterpart to
     * `co_checkout_started`. A separate event rather than a `sessionType` property,
     * because preorder is reported as its own funnel and collects different fields.
     *
     * `surface` stays `checkout`: preorder runs inside the checkout app, so the event
     * carries the checkout base props. The `po_` prefix separates the funnel, not the
     * app.
     *
     * Shares `co_checkout_started`'s trigger — the `checkout.fe.skeleton.dismissed`
     * mark, with the same bounded fallback — so "started" cannot mean one moment on
     * one funnel and a different moment on the other. Dedupe is once per visit, held
     * on the tracker instance so a remount of the checkout tree cannot double-report.
     *
     * CDP-only. The ad-platform entry signal for a preorder session rides
     * `co_checkout_started`'s pixel fan-out historically; whether preorder should have
     * its own ad entry event is open, and belongs with the preorder audit.
     * @dedupe Once per visit.
     */
    PREORDER_CHECKOUT_STARTED: {
        canonical: 'po_checkout_started',
        wireName: 'buyer_began_preorder_checkout',
        surface: 'checkout',
        description: 'Preorder checkout finished loading and is ready to use: every loading placeholder has been replaced with real content. Fires once per visit.',
        schema: z.object({
            cartId: z.string().describe('The CheckoutCart being preordered.'),
            numVehicles: z.number().describe('How many vehicles are in the cart.'),
            numAccessories: z
                .number()
                .describe('Total accessory count across the cart, summing per-accessory quantities.'),
            totalCartCost: z
                .number()
                .describe('Cart total in USD at the moment preorder checkout became usable.'),
            vehicleProductIds: z
                .array(z.string())
                .describe('Product id of each vehicle in the cart, for joining to inventory.'),
            ...RESOLVED_PREORDER_STATE,
            trigger: z
                .enum(['skeleton_dismissed', 'fallback_totals_derived', 'first_block_painted'])
                .describe('Which path fired the event. `fallback_totals_derived` means the skeleton-dismissed mark never arrived. `first_block_painted` is Express, which fires when its first step paints.'),
        }),
    },
    CHECKOUT_BUYER_PRESSED_ORDER: {
        canonical: 'co_order_submitted',
        wireName: 'checkout_buyer_pressed_order',
        surface: 'checkout',
        description: 'Buyer pressed Place Order. Carries everything they had chosen at that moment, so the order can be read without replaying the selection events.',
        schema: z.object({
            cartId: z.string().describe('The cart being submitted.'),
            totalCartCost: z.number().describe('Cart total in USD at submission.'),
            ...SUBMITTED_SELECTIONS,
            deliveryOption: DELIVERY_OPTION.describe('The fulfilment they submitted with.'),
            tarOption: TAR_OPTION.optional().describe('The paperwork handling they submitted with. Absent when the supported set never resolved, which happens while there is no registration address to derive it from.'),
            pickupLocationId: z
                .string()
                .optional()
                .describe('Where they collect. Absent unless the fulfilment is pickup.'),
        }),
    },
    PREORDER_BUYER_PRESSED_ORDER: {
        canonical: 'po_order_submitted',
        wireName: 'preorder_buyer_pressed_order',
        surface: 'checkout',
        description: 'Preorder buyer pressed Place Preorder. Carries what they had chosen at that moment. No fulfilment or paperwork option: preorder renders neither.',
        schema: z.object({
            cartId: z.string().describe('The cart being submitted.'),
            totalCartCost: z.number().describe('Cart total in USD at submission.'),
            paymentOption: PREORDER_PAYMENT_OPTION.describe('The reservation option they submitted with, whether they chose it or it was preselected.'),
            disclosuresAgreed: z
                .boolean()
                .describe('Whether the disclosures box was ticked at submission. The authoritative consent state — the toggle event is capped and cannot answer this.'),
            appliedPromotionIds: z
                .array(z.string())
                .describe('Promotions the preorder carried, so revenue effects attribute to them.'),
        }),
    },
    /** The server-confirmed conversion milestone is `credit_app_submitted`. */
    /**
     * The financed buyer's commitment, and the counterpart to
     * `co_order_submitted` in the cash flow.
     *
     * It carries `SUBMITTED_SELECTIONS` for that reason: pressing Continue into
     * the application is not a commitment — it submits nothing and the buyer can
     * still back out — so the authoritative snapshot of what they chose belongs
     * here, on the press that does commit them. Both flows therefore have one
     * event stating what the buyer agreed to, reached by different triggers, as
     * convention 3 describes.
     */
    CHECKOUT_BUYER_PRESSED_SUBMIT_CREDIT_APP: {
        canonical: 'co_credit_app_submitted',
        wireName: 'checkout_buyer_pressed_submit_credit_app',
        surface: 'checkout',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        // The financing workflow's payment step. A financed deal is placed with
        // nothing due today and the buyer enters payment details nowhere, so this
        // press is the moment their payment method is settled. Without the map, the
        // external payment step is missing for every financed buyer while card and
        // Klarna buyers report it from `co_payment_info_completed` \u2014 the same
        // external step reached by different triggers per workflow.
        adEventNames: {
            meta: 'AddPaymentInfo',
            tiktok: 'AddPaymentInfo',
            tracklution: 'payment_info_submitted',
            gtm: 'add_payment_info',
        },
        description: 'Buyer pressed Submit on the credit application and it passed the form\u2019s own validation. A rejected press reports `co_credit_app_failed` instead.',
        schema: z.object({
            cartId: z.string().describe('The cart being financed.'),
            totalCartCost: z.number().describe('Cart total in USD at submission.'),
            ...SUBMITTED_SELECTIONS,
        }),
        ecommerceSchema: ECOMMERCE_PAYLOAD,
    },
    // -------------------------------------------------------------------------
    // Carthage funnel entries—one event, translated per sink. PostHog/Segment
    // receive the `carth_*` canonical; ad platforms receive the `adEventNames`
    // translations via ConversionTracking.
    // -------------------------------------------------------------------------
    CARTHAGE_GO_TO_CHECKOUT_CLICKED: {
        canonical: 'carth_go_to_checkout_clicked',
        wireName: 'carthage_go_to_checkout_clicked',
        surface: 'carthage',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'AddToCart',
            tiktok: 'AddToCart',
            tracklution: null,
            gtm: 'add_to_cart',
        },
        description: 'Buyer pressed a checkout CTA on a listing.',
        schema: z.object({
            listingId: z.string(),
            paymentSelection: z.string(),
            listingPrice: z.number(),
            financing: z.enum(['enabled', 'disabled', 'unknown']),
            location: z.string(),
            component: z.string(),
        }),
    },
    CARTHAGE_INQUIRY_SUBMITTED: {
        canonical: 'carth_inquiry_submitted',
        wireName: 'carthage_inquiry_submitted',
        surface: 'carthage',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'Lead',
            tiktok: null,
            tracklution: 'lead',
            gtm: 'generate_lead',
        },
        description: 'Buyer submitted a lead inquiry on a listing.',
        schema: z.object({
            listingId: z.string(),
            make: z.string().optional(),
            model: z.string().optional(),
            modelYear: z.number().optional(),
            advertisedPrice: z.number().optional(),
            vehicleCategory: z.string().optional(),
            condition: z.string().optional(),
        }),
    },
    /**
     * A finance lead, not a Meta/TikTok conversion, so `meta`/`tiktok` are unset;
     * it fans out as a Tracklution lead with the buyer's contact info riding
     * `opts.contactInfo`. The dataLayer leg keeps the dealer-facing `Pre-Qualified`
     * name so dealer GTM triggers keyed on it keep firing.
     */
    CARTHAGE_PREQUAL_FORM_SUBMITTED: {
        canonical: 'carth_prequal_form_submitted',
        wireName: 'carthage_prequal_form_submitted',
        surface: 'carthage',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            tracklution: 'lead',
            gtm: 'Pre-Qualified',
        },
        description: 'Buyer submitted the finance pre-qualification form.',
        schema: z.object({
            leadType: z.literal('ApplyForFinance'),
            outcome: z.string(),
            fileNumber: z.string().optional(),
        }),
    },
};

export { EXPRESS_GATE_DECIDED_OUTCOME, EXPRESS_GATE_OUTCOME, FUNNEL_EVENTS };
//# sourceMappingURL=funnel.js.map
